import { ReactNode } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { TrackedCard } from '../../redux/api/kit-staging';

/** Map a TrackedCard status to an AntD tag color. */
export function statusColor(status: string): string {
	switch (status) {
		case 'ready':
		case 'ready_priority':
			return 'success';
		case 'setup_pending':
			return 'default';
		case 'pending_placement':
		case 'pending_removal':
			return 'processing';
		case 'on_hold':
			return 'purple';
		case 'cancelled':
		case 'site_mismatch':
			return 'error';
		case 'task_status_mismatch':
		case 'issue':
			return 'warning';
		case 'waiting_kit':
		case 'waiting_material':
		case 'waiting_both':
		default:
			return 'default';
	}
}

/** Map a TrackedCard status to a concrete hex colour for solid blocks. */
export function statusHex(status: string): string {
	// Setup Pending is dark grey per spec ("Card turns dark grey 'SETUP PENDING'"),
	// distinct from the blue "processing" / green "ready" treatments.
	if (status === 'setup_pending') return '#595959';
	switch (statusColor(status)) {
		case 'success':
			return '#52c41a';
		case 'processing':
			return '#1890ff';
		case 'purple':
			return '#722ed1';
		case 'error':
			return '#ff4d4f';
		case 'warning':
			return '#faad14';
		default:
			return '#8c8c8c';
	}
}

const AREA_LABEL: Record<string, string> = {
	unassigned: 'Unassigned',
	cart: 'Cart',
	msa: 'MSA',
	jsa: 'JSA',
	dekit: 'Dekit',
};

const CARD_HEIGHT = 130;
const BAR_WIDTH = 54;

const formatRuntime = (minutes: number | null): string => {
	if (minutes == null) return '—';
	const h = Math.floor(minutes / 60);
	const m = minutes % 60;
	return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatDate = (iso: string | null): string => {
	if (!iso) return '—';
	const d = new Date(iso);
	const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	return `${days[d.getDay()]}, ${months[d.getMonth()]}-${d.getDate()}`;
};

interface Props {
	card: TrackedCard;
	onSelect: (card: TrackedCard) => void;
	/** Retained for API compatibility — actions now live in the detail panel. */
	onMove?: (card: TrackedCard) => void;
	onDelete?: (card: TrackedCard) => Promise<void> | void;
	selected?: boolean;
}

/** A single TrackedCard rendered as the legacy 130px split-bar job card. */
export function TrackedCardTile({ card, onSelect, selected }: Props) {
	const job = card.job ?? null;
	const isCancelled = card.status === 'cancelled';
	const isErrorBorder = isCancelled || card.status === 'site_mismatch';

	const mgColor = job?.machine_group?.color || '#8c8c8c';
	const mgName = job?.machine_group?.name || '—';
	const areaLabel = (AREA_LABEL[card.current_area] ?? card.current_area).toUpperCase();
	const itemBg = card.item_type === 'kit' ? '#1890ff' : '#2f54eb';
	const priority = card.priority ?? null;

	return (
		<div
			className="rounded-lg overflow-hidden cursor-pointer select-none flex flex-col"
			style={{
				height: CARD_HEIGHT,
				backgroundColor: '#fefefe',
				border: `1.5px solid ${isErrorBorder ? '#ff4d4f' : '#e5e7eb'}`,
				outline: selected ? '2px solid #1890ff' : undefined,
			}}
			tabIndex={0}
			role="button"
			onClick={() => onSelect(card)}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onSelect(card);
				}
			}}
		>
			{/* TOP SECTION: split bar + body */}
			<div className="flex flex-1 min-h-0">
				{/* LEFT SPLIT BAR */}
				<div className="flex flex-col flex-shrink-0" style={{ width: BAR_WIDTH }}>
					{/* Top half: machine group color + name */}
					<div
						className="flex items-center justify-center text-center px-1 flex-1"
						style={{ backgroundColor: mgColor }}
					>
						<span className="font-bold text-white leading-tight" style={{ fontSize: 11 }}>
							{mgName}
						</span>
					</div>
					{/* Bottom half: status color + priority badge + area label */}
					<div
						className="flex flex-col items-center justify-center text-center px-1 flex-1 gap-1"
						style={{ backgroundColor: statusHex(card.status) }}
					>
						{priority != null && (
							<div
								className="flex items-center justify-center rounded-full font-black text-white"
								style={{
									backgroundColor: mgColor,
									fontSize: 16,
									width: 26,
									height: 26,
									border: '2px solid rgba(255,255,255,0.5)',
								}}
							>
								{priority}
							</div>
						)}
						<span
							className="font-bold text-white"
							style={{ fontSize: 10, writingMode: 'vertical-rl', letterSpacing: 1 }}
						>
							{areaLabel}
						</span>
					</div>
				</div>

				{/* MAIN BODY */}
				<div className="flex-1 min-w-0 px-2 py-1.5 flex flex-col justify-between">
					<div className="flex gap-1.5">
						<div
							className="flex-shrink-0 rounded bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200/60"
							style={{ width: 46, height: 46 }}
						>
							{job?.thumbnail ? (
								<img src={job.thumbnail} alt={job.part_name} className="w-full h-full object-cover" />
							) : (
								<InboxOutlined style={{ fontSize: 20, color: '#d1d5db' }} />
							)}
						</div>
						<div className="flex-1 min-w-0">
							<div className="font-bold text-gray-900 truncate" style={{ fontSize: 13 }}>
								{job?.job_number || `OLI #${card.order_line_item_id}`}
								{isCancelled && (
									<span className="ml-1 font-bold text-red-500" style={{ fontSize: 10 }}>
										⚠ cancelled
									</span>
								)}
							</div>
							<div className="text-gray-700 truncate leading-tight" style={{ fontSize: 11 }}>
								{job?.part_name || ''}
							</div>
							<div className="text-gray-500 truncate" style={{ fontSize: 10 }}>
								{job?.quantity ?? '—'} pcs · {job?.material || ''}
								{job?.finish ? ` · ${job.finish}` : ''}
							</div>
							{job?.material_partial && (
								<div style={{ fontSize: 9, fontWeight: 700, color: '#faad14' }}>◐ Partial material</div>
							)}
						</div>
					</div>

					<div className="flex justify-between items-end mt-1">
						<div>
							<div className="text-gray-400 uppercase" style={{ fontSize: 8 }}>
								Start
							</div>
							<div className="text-gray-700 font-medium" style={{ fontSize: 11 }}>
								{formatDate(job?.scheduled_start ?? null)}
							</div>
						</div>
						<div className="text-right">
							<div className="text-gray-400 uppercase" style={{ fontSize: 8 }}>
								Mach. Time
							</div>
							<div className="text-gray-700 font-medium" style={{ fontSize: 11 }}>
								{formatRuntime(job?.est_machine_minutes ?? null)}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* BOTTOM STRIP: item type + status */}
			<div className="flex flex-shrink-0" style={{ height: 22 }}>
				<div
					className="flex-1 flex items-center justify-center font-bold truncate px-1"
					style={{ backgroundColor: itemBg, color: '#fff', fontSize: 10 }}
				>
					{card.item_type.toUpperCase()}
				</div>
				<div style={{ width: 1, backgroundColor: '#fff' }} />
				<div
					className="flex-1 flex items-center justify-center font-bold truncate px-1"
					style={{ backgroundColor: statusHex(card.status), color: '#fff', fontSize: 10 }}
				>
					{card.status}
				</div>
			</div>
		</div>
	);
}

/** Responsive grid wrapper for tiles. */
export function CardGrid({ children }: { children: ReactNode }) {
	return (
		<div
			style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
				gap: 16,
				marginTop: 16,
			}}
		>
			{children}
		</div>
	);
}
